import { Countries } from '@xoda/common'
import i18next from 'i18next'
import { FC, Key, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'
import { Dirty } from '../../helpers/dirty'
import { createBooleanDataset, toCapitalized } from '../../helpers/others'
import { ReactComponent as ArrowDown1 } from '../../icons/arrow-down-1.svg'
import { PlanService } from '../../services/plan'
import { gymState } from '../../store/gym'
import { Button } from '../Button'
import s from './Goods.module.css'

const Goods: FC<GoodsProps> = (props) => {
  const { items, defaultSelected, large = false, onSelect } = props

  const { t } = useTranslation()
  const [gym] = useRecoilState(gymState)

  const [selected, setSelected] = useState<Key | undefined>(defaultSelected)
  const [foldings, setFoldings] = useState<Record<string, boolean>>({})

  const createOnItemClick = (item: GoodsItem) => () => {
    setSelected(item.key)
    onSelect?.(item)
  }

  const createHandleFold = (key: string) => () => {
    setFoldings((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const renderItems = (items: GoodsItem[]) => {
    return (
      <ul className={s.list} {...createBooleanDataset({ large })}>
        {items.map((item) => (
          <li
            key={item.key}
            className={s.item}
            {...createBooleanDataset({ selected: item.key === selected })}
            onClick={createOnItemClick(item)}
          >
            {large && <div className={s.type}>{toCapitalized(item.type)}</div>}
            <div className={s.info}>
              <div className={s.price}>
                <div className={s['price-number']}>
                  {Countries.formatAmount(item.price, gym?.currency_code, true)}
                </div>
                {item.per && !Dirty.isPriceTermHidden(gym?.id) && (
                  <div className={s.per}>
                    {t(
                      item.type === 'plan' && PlanService.isOnceOff(item.dto)
                        ? 'for {{unit}}'
                        : 'per {{unit}}',
                      { unit: item.per }
                    )}
                  </div>
                )}
              </div>
              <div>
                <div className={s.name}>
                  {item.name}{' '}
                  {item.type === 'pack' &&
                    `×${t('classWithCount', { count: item.classesNumber })}`}
                </div>
                {item.duration && item.durationUnit && (
                  <div className={s.duration}>
                    {t('Valid for {{duration}}', {
                      duration: t(`${item.durationUnit}WithCount`, {
                        count: item.duration,
                      }),
                    })}
                  </div>
                )}
                {item.type === 'plan' && item.trialPeriod && (
                  <div className={s.duration}>
                    {t('{{duration}} for trial', {
                      duration: t('dayWithCount', { count: item.trialPeriod }),
                    })}
                  </div>
                )}
              </div>
              {large && <div className={s.description}>{item.description}</div>}
            </div>
            {large && (
              <dl className={s.data}>
                {item.type === 'plan' && (
                  <>
                    {!!item.joinFee && (
                      <>
                        <dt>{t('Joining fee')}:</dt>
                        <dd>
                          {Countries.formatAmount(
                            item.joinFee,
                            gym?.currency_code,
                            true
                          )}
                        </dd>
                      </>
                    )}
                    {!!item.suspensionFee && (
                      <>
                        <dt>{t('Suspension fee')}:</dt>
                        <dd>
                          {Countries.formatAmount(
                            item.suspensionFee,
                            gym?.currency_code,
                            true
                          )}
                          {item.per &&
                            !Dirty.isPriceTermHidden(gym?.id) &&
                            ` ${t('per {{unit}}', { unit: item.per })}`}
                        </dd>
                      </>
                    )}
                  </>
                )}
              </dl>
            )}
            <div className={s.select}>
              {item.key === selected ? (
                <Button variant="ghost" color="black">
                  {t('selected')}
                </Button>
              ) : (
                <Button variant="ghost" color="white">
                  {t('select')}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    )
  }

  return Array.isArray(items) ? (
    renderItems(items)
  ) : (
    <>
      {Object.entries(items).map(([title, groupItems]) => (
        <section
          key={title}
          className={s.group}
          {...createBooleanDataset({ folded: foldings[title] })}
        >
          <h2 className={s['group-title']} onClick={createHandleFold(title)}>
            {t(title)}
            <ArrowDown1 className={s.folder} />
          </h2>
          {renderItems(groupItems)}
        </section>
      ))}
    </>
  )
}

const mapPacks = (dtoPacks: Dto.Pack[]): GoodsItem[] =>
  dtoPacks.map((dtoPack) => ({
    key: dtoPack.id,
    type: 'pack',
    dto: dtoPack,
    price: dtoPack.price,
    name: dtoPack.name,
    description: dtoPack.description,
    classesNumber: dtoPack.pack_session_no,
    duration: dtoPack.pack_duration,
    durationUnit: dtoPack.pack_type,
    services: dtoPack.gym_pack_categories.map(
      (gymPackCategory) => gymPackCategory.gym_category.category.name
    ),
  }))

const mapProducts = (dtoProducts: Dto.Product[]): GoodsItem[] =>
  dtoProducts.map((dtoProduct) => ({
    key: dtoProduct.id,
    type: 'product',
    dto: dtoProduct,
    price: dtoProduct.price,
    name: dtoProduct.name,
    description: dtoProduct.description,
  }))

const mapPlans = (dtoPlans: Dto.Plan[]): GoodsItem[] =>
  dtoPlans.map((dtoPlan) => {
    if (dtoPlan.payment_schedule === 'full') {
      return {
        key: dtoPlan.id,
        type: 'plan',
        dto: dtoPlan,
        price: dtoPlan.full_price,
        joinFee: dtoPlan.join_fee || undefined,
        name: dtoPlan.name,
        description: dtoPlan.description,
        duration: dtoPlan.plan_duration,
        durationUnit: dtoPlan.plan_type.replace(/ly$/, ''),
        trialPeriod: dtoPlan.trial_days || undefined,
        suspensionFee: getSuspensionFee(dtoPlan),
      }
    }

    const price = getPlanPrice(dtoPlan)
    return {
      key: dtoPlan.id,
      type: 'plan',
      dto: dtoPlan,
      price: price.number,
      joinFee: dtoPlan.join_fee || undefined,
      name: dtoPlan.name,
      description: dtoPlan.description,
      per: price.per,
      trialPeriod: dtoPlan.trial_days || undefined,
      suspensionFee: getSuspensionFee(dtoPlan),
    }
  })

const getPlanPrice = (dtoPlan: Dto.Plan): { number: number; per: string } => {
  if (dtoPlan.version === 1) {
    const {
      member_fee: { amount: price },
      term: { type, length },
    } = dtoPlan.details!.payment

    return {
      number: price,
      per: i18next.t(
        PlanService.isOnceOff(dtoPlan) || length > 1
          ? `${type}WithCount`
          : type,
        {
          count: Number(length),
        }
      ),
    }
  }

  switch (dtoPlan.payment_schedule) {
    case null:
    case 'weekly':
      return {
        number: dtoPlan.price!,
        per: i18next.t('week', { count: 1 }),
      }

    case 'fortnightly':
      return {
        number: dtoPlan.fort_nightly_price,
        per: i18next.t('weekWithCount', { count: 2 }),
      }

    case 'monthly':
      return {
        number: dtoPlan.monthly_price,
        per: i18next.t('month', { count: 1 }),
      }

    default:
      throw new Error(
        `Unknown payment schedule \`${dtoPlan.payment_schedule}\``
      )
  }
}

const getSuspensionFee = (plan: Dto.Plan) => {
  const { version, details, payment_schedule } = plan

  if (!version) {
    switch (payment_schedule) {
      case 'weekly':
      case 'full':
        return plan.weekly_suspension_fee

      case 'fort_nightly':
        return plan.fort_nightly_suspension_fee

      case 'monthly':
        return plan.monthly_suspension_fee

      default:
        return 0
    }
  }

  return details?.payment.suspension_fee?.amount || 0
}

const isPlan = (item: any): item is Dto.Plan => !!item && 'plan_type' in item

const isPack = (item: any): item is Dto.Pack => !!item && 'pack_type' in item

const isProduct = (item: any): item is Dto.Product =>
  !!item && 'in_stock' in item

const getGoodsType = (item: GoodsItem['dto']) =>
  isPlan(item)
    ? 'plan'
    : isPack(item)
    ? 'pack'
    : isProduct(item)
    ? 'product'
    : undefined

type GoodsProps = {
  items: GoodsItem[] | Record<string, GoodsItem[]>
  defaultSelected?: Key
  large?: boolean
  onSelect?: (item: GoodsItem) => void
}

type GoodsItem = {
  key: Key
  price: number
  name: string
  description?: string
  per?: string
  duration?: number
  durationUnit?: string
} & (
  | {
      type: 'plan'
      dto: Dto.Plan
      joinFee: number | undefined
      suspensionFee: number
      trialPeriod: number | undefined
    }
  | {
      type: 'pack'
      dto: Dto.Pack
      classesNumber: number
      services: string[]
    }
  | {
      type: 'product'
      dto: Dto.Product
    }
)

export {
  getGoodsType,
  getPlanPrice,
  Goods,
  isPack,
  isPlan,
  isProduct,
  mapPacks,
  mapPlans,
  mapProducts,
}
export type { GoodsItem, GoodsProps }
